"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  ShoppingCart,
  Store,
  Truck,
} from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import ShopProductCard, { ProductPhoto, ShopProduct } from "../ShopProductCard";
import { WhatsAppLink } from "../StoreLinks";
import { useCart } from "../../context/cartContext";
import { addToCartGA, viewItemGA } from "../../../../lib/analytics";
import {
  categoryPath,
  formatCategory,
  formatProductDescription,
  formatProductName,
} from "../../../../lib/catalog";

export default function ProductDetailClient({
  product,
  relatedProducts,
}: {
  product: ShopProduct;
  relatedProducts: ShopProduct[];
}) {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selected, setSelected] = useState(0);
  const [pending, setPending] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    viewItemGA(product);
    setQuantity(1);
    setSelected(0);
    setAdded(false);
    setError("");
  }, [product]);
  const available = product.stock > 0;
  const productName = formatProductName(product.name);
  const productDescription = formatProductDescription(product.shortDescription);
  const categoryName = formatCategory(product.category);
  const add = async () => {
    if (pending || !available) return;
    setPending(true);
    setError("");
    setAdded(false);
    try {
      const result = await addToCart(product.id, quantity);
      if (result.success) {
        setAdded(true);
        addToCartGA(product.name, quantity);
      } else
        setError(result.error || "Unable to add this item. Please try again.");
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="store-site">
      <Header />
      <main id="main-content" className="store-container product-detail">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link href="/">Home</Link>
          <span>›</span>
          <Link href="/products">Products</Link>
          <span>›</span>
          <Link
            href={categoryPath(categoryName)}
          >
            {categoryName}
          </Link>
        </nav>
        <div className="product-detail-grid">
          <div>
            <div className="product-main-photo">
              <ProductPhoto
                key={product.images[selected]}
                src={product.images[selected]}
                alt={productName}
                priority
                sizes="(min-width: 768px) 50vw, 100vw"
              />
              {product.images.length > 1 && (
                <>
                  <button
                    className="photo-prev"
                    aria-label="Previous product photo"
                    onClick={() =>
                      setSelected(
                        (selected + product.images.length - 1) %
                          product.images.length,
                      )
                    }
                  >
                    <ChevronLeft />
                  </button>
                  <button
                    className="photo-next"
                    aria-label="Next product photo"
                    onClick={() =>
                      setSelected((selected + 1) % product.images.length)
                    }
                  >
                    <ChevronRight />
                  </button>
                </>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="product-thumbnails">
                {product.images.map((src, index) => (
                  <button
                    key={`${src}-${index}`}
                    aria-label={`Show product photo ${index + 1}`}
                    aria-pressed={selected === index}
                    onClick={() => setSelected(index)}
                  >
                    <ProductPhoto
                      src={src}
                      alt={`${productName}, view ${index + 1}`}
                      sizes="90px"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="product-detail-copy">
            <p className="product-category">{categoryName}</p>
            <h1 className="editorial-title">{productName}</h1>
            <div className="detail-price">
              AED {product.discountedPrice.toFixed(2)}
              {product.originalPrice > product.discountedPrice && (
                <del>AED {product.originalPrice.toFixed(2)}</del>
              )}
            </div>
            <p
              className={`stock-status ${available ? "available" : "unavailable"}`}
            >
              {available
                ? `In stock · ${product.stock} available`
                : "Out of stock"}
            </p>
            {productDescription && (
              <p className="product-description">{productDescription}</p>
            )}
            {available ? (
              <div className="detail-purchase">
                <div>
                  <label id="quantity-label">Quantity</label>
                  <div
                    className="quantity-control"
                    role="group"
                    aria-labelledby="quantity-label"
                  >
                    <button
                      aria-label="Decrease quantity"
                      disabled={quantity <= 1 || pending}
                      onClick={() => setQuantity(quantity - 1)}
                    >
                      <Minus size={16} />
                    </button>
                    <output aria-live="polite">{quantity}</output>
                    <button
                      aria-label="Increase quantity"
                      disabled={quantity >= product.stock || pending}
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                <button
                  className="store-button primary"
                  disabled={pending}
                  onClick={add}
                >
                  <ShoppingCart size={19} />
                  {pending ? "Adding…" : "Add to cart"}
                </button>
              </div>
            ) : (
              <div className="out-of-stock-actions">
                <WhatsAppLink
                  label="Check store availability"
                  source="out_of_stock_product"
                  message={`Hello Shop House, ${productName} is shown as out of stock on your website. Is it available in store, or do you have a similar alternative?`}
                />
                <Link
                  href={categoryPath(categoryName)}
                  className="store-button outline"
                >
                  View similar products
                </Link>
              </div>
            )}
            <div aria-live="polite">
              {added && (
                <p className="add-success">
                  Added {quantity} to your cart.{" "}
                  <Link href="/cart">View cart →</Link>
                </p>
              )}
              {error && (
                <p className="add-error" role="alert">
                  {error}
                </p>
              )}
            </div>
            <div className="fulfillment-card">
              <div>
                <Store />
                <p>
                  <strong>Collect from our store</strong>Choose free store
                  pickup at checkout.
                </p>
              </div>
              <div>
                <Truck />
                <p>
                  <strong>Delivery in Al Ain</strong>Delivery options and
                  charges are shown at checkout.
                </p>
              </div>
              <div>
                <p>
                  <strong>Need a little help?</strong>Ask about this product or
                  bulk quantities.
                </p>
              </div>
              <WhatsAppLink
                label="Ask on WhatsApp"
                source="product"
                message={`Hello Shop House, I would like to ask about ${productName}.`}
              />
            </div>
          </div>
        </div>
        {relatedProducts.length > 0 && (
          <section className="related-products">
            <div className="section-heading">
              <h2 className="editorial-title">You might also like</h2>
              <Link href="/products" className="text-link">
                View all products →
              </Link>
            </div>
            <div className="shop-product-grid">
              {relatedProducts.map((item) => (
                <ShopProductCard key={item.id} product={item} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
